import type { NotificationKind } from '@salonomy/types';

/**
 * WS-14 — NOTIFICATIONS INBOX. Pure, unit-testable presentation helpers for the inbox: the kind labels
 * (the unread filter chips + the per-item tag) and the entity → console-route deep-link. Kept apart
 * from the React components so the logic is trivially testable with no DOM.
 */

/** A short, human label per curated kind — drives the kind tag + the filter chips. */
export const KIND_LABEL: Record<NotificationKind, string> = {
  low_stock: 'Low stock',
  online_booking: 'Online booking',
  comms_at_cap: 'Messaging cap',
  no_show: 'No-show',
};

/** The four curated kinds, in the order the filter renders them. */
export const KIND_ORDER: NotificationKind[] = [
  'low_stock',
  'online_booking',
  'comms_at_cap',
  'no_show',
];

/**
 * The deep-link for a notification, derived from its (entityType, kind). We ONLY ever return a route
 * that exists in the console router — never an invented one. The entityId is a SOFT reference (the
 * entity may have been erased), so we link to the relevant LIST/section, not a per-row detail route the
 * console doesn't have:
 *   • product (low_stock)            → /stock   (the stock levels surface)
 *   • appointment (online_booking,   → /        (the diary)
 *     no_show)
 *   • comms (comms_at_cap)           → /comms   (the comms/allowance surface)
 * Returns null when there is nothing to link to.
 */
export function notificationLink(n: {
  kind: NotificationKind;
  entityType: 'product' | 'appointment' | 'comms' | null;
}): string | null {
  switch (n.entityType) {
    case 'product':
      return '/stock';
    case 'appointment':
      return '/';
    case 'comms':
      return '/comms';
    default:
      return null;
  }
}

/**
 * A compact relative-time label for a notification's createdAt (e.g. "just now", "5m", "3h", "2d"),
 * falling back to an absolute short date past a week. Pure: takes `now` so it is deterministic in tests.
 */
export function relativeTime(iso: string, now: number = Date.now()): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const diffSec = Math.max(0, Math.round((now - then) / 1000));
  if (diffSec < 45) return 'just now';
  const diffMin = Math.round(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m`;
  const diffHr = Math.round(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h`;
  const diffDay = Math.round(diffHr / 24);
  if (diffDay < 7) return `${diffDay}d`;
  return new Date(then).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
}
